import { useTranslation } from 'react-i18next';

import { getTenantPhoneNumber } from 'theme/selectors';
import Button, { ButtonSize } from 'components/Button';
import { useAppDispatch, useAppSelector } from 'hooks';
import Alert, { AlertTypes } from 'components/Alert';
import { getBusiness } from 'pages/Profile/selectors';
import Badge from 'components/Badge';
import {
  FunderOnboardingStatuses,
  ApplicationStatuses,
} from 'entities/progress';

import { setShowOatFiOnboardingModal } from '../submitDashboardSlice';

const LineOfCredit = () => {
  const { t } = useTranslation('dashboard');
  const dispatch = useAppDispatch();

  const business = useAppSelector(getBusiness);
  const tenantPhoneNumber = useAppSelector(getTenantPhoneNumber);

  const handleApply = async () => {
    dispatch(setShowOatFiOnboardingModal(true));
  };

  const funderOnboardingStatus = (
    business.funderOnboardingStatusName ?? ''
  ).toLowerCase();

  const isApproved =
    business.applicationStatusTypeCode === ApplicationStatuses.approved;

  return (
    <div className="p-6 flex flex-col flex-1">
      <p className="heading-2xl mb-1">{t('submitModal.lineOfCredit.title')}</p>
      <p className="text-secondary mb-9 whitespace-pre-wrap">
        {t('submitModal.lineOfCredit.text')}
      </p>

      <div className="flex-1 mb-6">
        <div className="px-4 py-3 bg-gray-50 rounded-lg flex items-start max-w-[768px]">
          <div className="mr-2 rounded-md bg-white p-2">
            <img
              src="/assets/images/oatFi.svg"
              alt="oatFi"
              className="flex-shrink-0"
            />
          </div>

          <div className="flex-1 mr-6">
            <p className="text-sm font-semibold mb-1">
              {t('submitModal.lineOfCredit.account')}
            </p>
            <p className="text-secondary">
              {t('submitModal.lineOfCredit.accountText')}
            </p>
          </div>

          {(!business.funderOnboardingStatusName ||
            funderOnboardingStatus === FunderOnboardingStatuses.pending ||
            funderOnboardingStatus ===
              FunderOnboardingStatuses.approvedNotSigned) && (
            <Button
              className="self-center"
              size={ButtonSize.medium}
              onClick={handleApply}
            >
              {t('submitModal.lineOfCredit.apply')}
            </Button>
          )}

          {funderOnboardingStatus ===
            FunderOnboardingStatuses.pendingApproval && (
            <Badge
              rounded="rounded-sm"
              size="large"
              className="px-2 py-0.5 border-blue-200 border self-center"
              color="blue"
            >
              {t('progress.capital.pending')}
            </Badge>
          )}

          {funderOnboardingStatus === FunderOnboardingStatuses.declined && (
            <Badge
              rounded="rounded-sm"
              size="large"
              className="px-2 py-0.5 border-red-200 border self-center"
              color="red"
            >
              {t('progress.capital.declined')}
            </Badge>
          )}

          {funderOnboardingStatus === FunderOnboardingStatuses.approved && (
            <Badge
              rounded="rounded-sm"
              size="large"
              className={`px-2 py-0.5 border-${
                isApproved ? 'green' : 'blue'
              }-200 border self-center`}
              color={isApproved ? 'green' : 'blue'}
            >
              {t(`progress.capital.${isApproved ? 'active' : 'approved'}`)}
            </Badge>
          )}

          {funderOnboardingStatus ===
            FunderOnboardingStatuses.approvedNotSigned && (
            <Badge
              rounded="rounded-sm"
              size="large"
              className="px-2 py-0.5 border-yellow-200 border self-center ml-2"
              color="yellow"
            >
              {t('progress.capital.actionRequired')}
            </Badge>
          )}
        </div>

        {funderOnboardingStatus ===
          FunderOnboardingStatuses.approvedNotSigned && (
          <div className="flex">
            <Alert
              type={AlertTypes.warning}
              className="mt-6"
              title={t('submitModal.lineOfCredit.supportText', {
                phone: tenantPhoneNumber,
              })}
              icon={AlertTypes.empty}
            />
          </div>
        )}
      </div>

      <div className="mt-auto text-secondary">
        {t('submitModal.confirmInfo')}
      </div>
    </div>
  );
};

export default LineOfCredit;
